import { useCallback, useEffect, useRef } from "react";

const EASE = 0.18; // position follow
const FADE = 0.12; // enter/leave ramp
const SETTLED = 0.002;

/**
 * Tracks the pointer inside a hero element and writes its position and an
 * on/off ramp to CSS custom properties (--spot-x / --spot-y / --spot-on).
 *
 * The ramp is eased in JS rather than with a CSS transition because the value
 * feeds a mask-image radius, which browsers do not interpolate.
 *
 * There is no coarse-pointer branch: the CSS keeps a resting pool of light
 * open at --spot-on 0, so touch devices still see the artwork.
 *
 * Returns { ref, onPointerMove, onPointerLeave } — spread the handlers onto
 * the hero wrapper.
 */
function useSpotlight() {
  const ref = useRef(null);
  const frame = useRef(null);
  const target = useRef({ x: 68, y: 45, on: 0 });
  const current = useRef({ x: 68, y: 45, on: 0 });

  const tick = useCallback(() => {
    const node = ref.current;
    if (!node) {
      frame.current = null;
      return;
    }

    const to = target.current;
    const at = current.current;
    at.x += (to.x - at.x) * EASE;
    at.y += (to.y - at.y) * EASE;
    at.on += (to.on - at.on) * FADE;

    node.style.setProperty("--spot-x", `${at.x.toFixed(2)}%`);
    node.style.setProperty("--spot-y", `${at.y.toFixed(2)}%`);
    node.style.setProperty("--spot-on", at.on.toFixed(4));

    const settled =
      Math.abs(to.x - at.x) < SETTLED &&
      Math.abs(to.y - at.y) < SETTLED &&
      Math.abs(to.on - at.on) < SETTLED;

    frame.current = settled ? null : requestAnimationFrame(tick);
  }, []);

  const start = useCallback(() => {
    if (frame.current == null) frame.current = requestAnimationFrame(tick);
  }, [tick]);

  useEffect(
    () => () => {
      if (frame.current != null) cancelAnimationFrame(frame.current);
    },
    []
  );

  const onPointerMove = useCallback(
    (event) => {
      const node = ref.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      target.current = {
        x: ((event.clientX - rect.left) / rect.width) * 100,
        y: ((event.clientY - rect.top) / rect.height) * 100,
        on: 1,
      };
      start();
    },
    [start]
  );

  const onPointerLeave = useCallback(() => {
    target.current = { ...target.current, on: 0 };
    start();
  }, [start]);

  return { ref, onPointerMove, onPointerLeave };
}

export default useSpotlight;
